import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Paper,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  ArrowForwardRounded,
  DirectionsCarOutlined,
  EventSeatOutlined,
  InfoOutlined,
  CheckRounded,
  RefreshRounded,
  PersonOutlineOutlined,
  CloseRounded,
  ShieldOutlined,
} from "@mui/icons-material";
import { useIntl } from "react-intl";
import moment from "moment-timezone";

import DashboardClientLayout from "../../Layouts/DashboardClientLayout";
import { setAuthentication } from "../../Redux/Authentication/actions";
import RideApi from "Services/Ride";
import BookingApi from "Services/Booking";
import loginApi from "../../Services/Login";
import PlaceService from "../../Services/Place";
import { hideLoader, showLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import "../TrajetClient/HomePage.css";

const recentFallback = [
  { from: "YaoundÃ©", to: "Douala", date: "Aujourd'hui" },
  { from: "Douala", to: "Bafoussam", date: "Hier" },
  { from: "LomÃ©", to: "Cotonou", date: "9 juin" },
];
 
const popularTrips = [
  { from: "YaoundÃ©", to: "Douala", price: "Ã  partir de 2 000 XAF", duration: "~ 2h 30m" },
  { from: "Douala", to: "Bafoussam", price: "Ã  partir de 2 500 XAF", duration: "~ 4h 15m" },
  { from: "YaoundÃ©", to: "Bafoussam", price: "Ã  partir de 3 000 XAF", duration: "~ 5h 30m" },
  { from: "LomÃ©", to: "Cotonou", price: "Ã  partir de 1 500 XAF", duration: "~ 1h 30m" },
];

function getPassengerCategoryOptions(t) {
  return [
    t("course.category.youth"),
    t("course.category.adult"),
    t("course.category.senior"),
  ];
}

const passengerAgeOptions = Array.from({ length: 99 }, (_, index) => String(index + 1));

function ArrowIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
      <path d="M5 12h14M12 5l7 7-7 7" />
    </svg>
  );
}

function CirclePinIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <rect x="3" y="4" width="18" height="17" rx="2.6" />
      <line x1="8" y1="2.5" x2="8" y2="6" />
      <line x1="16" y1="2.5" x2="16" y2="6" />
      <line x1="3" y1="9.5" x2="21" y2="9.5" />
    </svg>
  );
}

function ChevronDownIcon(props) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true" {...props}>
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function formatFrenchDate(date, mode = "long") {
  return new Intl.DateTimeFormat("fr-FR", {
    day: "numeric",
    month: mode === "short" ? "short" : "long",
  }).format(date);
}

function buildMonthGrid(date) {
  const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
  const startIndex = (firstDay.getDay() + 6) % 7;
  const totalDays = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const cells = [];

  for (let index = 0; index < startIndex; index += 1) {
    cells.push(null);
  }

  for (let day = 1; day <= totalDays; day += 1) {
    cells.push(new Date(date.getFullYear(), date.getMonth(), day));
  }

  return cells;
}

function formatPlaceResult(place) {
  const primary = place?.name || place?.addressName || place?.addressLocality || "Lieu";
  const secondaryParts = [
    place?.addressLocality,
    place?.addressRegion,
    place?.addressCountry,
  ].filter(Boolean);

  return {
    id: place?.id || place?._id || primary,
    title: primary,
    subtitle: secondaryParts.join(", "),
  };
}

function getAuthPayload(responseData) {
  return responseData?.data || responseData || {};
}

function getAuthToken(authData) {
  return authData?.accessToken || authData?.token || authData?.tokens?.accessToken || authData?.auth?.accessToken;
}

function persistAuthSession(authData) {
  const token = getAuthToken(authData);
  if (!token) return null;

  const user = authData?.user || {};
  localStorage.setItem("isAuthenticated", true);
  localStorage.setItem("token", token);
  localStorage.setItem("tokenExpiration", Date.now() + 24 * 60 * 60 * 1000);
  localStorage.setItem("userid", user?.id || authData?.id || "");
  localStorage.setItem("userType", user?.userType || authData?.userType || "");
  localStorage.setItem("email", user?.email || authData?.email || "");

  if (authData?.sub_companyName) {
    localStorage.setItem("subcompany", authData.sub_companyName);
  } else {
    localStorage.removeItem("subcompany");
  }

  return token;
}

function ReservationLoginModal({ open, onClose, form, setForm, onSubmit, loading, error }) {
  const { formatMessage } = useIntl();
  const t = (id, values) => formatMessage({ id }, values);

  if (!open) return null;

  return (
    <div className="ride-auth-overlay" role="presentation" onClick={onClose}>
      <div
        className="ride-auth-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ride-auth-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="ride-auth-accent" />
        <button type="button" className="ride-auth-close" onClick={onClose} aria-label={t("course.auth.close")}>Ã—</button>
        <div className="ride-auth-header">
          <span className="ride-auth-badge">{t("course.auth.badge")}</span>
          <h2 id="ride-auth-title">{t("course.auth.title")}</h2>
          <p>{t("course.auth.subtitle")}</p>
        </div>

        <form className="ride-auth-form" onSubmit={onSubmit}>
          <label className="ride-auth-field">
            <span>{t("course.auth.email")}</span>
            <input
              type="email"
              value={form.email}
              onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))}
              placeholder="exemple@email.com"
              autoComplete="email"
            />
          </label>

          <label className="ride-auth-field">
            <span>{t("course.auth.password")}</span>
            <input
              type="password"
              value={form.password}
              onChange={(event) => setForm((current) => ({ ...current, password: event.target.value }))}
              placeholder="â€¢â€¢â€¢â€¢â€¢â€¢â€¢â€¢"
              autoComplete="current-password"
            />
          </label>

          {error ? <div className="ride-auth-error">{error}</div> : null}

          <div className="ride-auth-actions">
            <button type="button" className="ride-auth-secondary" onClick={onClose}>{t("course.auth.cancel")}</button>
            <button type="submit" className="ride-auth-primary" disabled={loading}>
              {loading ? t("course.auth.loggingIn") : t("course.auth.loginAndBook")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const RecentCard = ({ item }) => (
  <Paper
    elevation={0}
    sx={{
      borderRadius: 4,
      border: "1px solid #e6ebf2",
      background: "#fff",
      px: 2.25,
      py: 1.75,
      boxShadow: "0 10px 24px rgba(8, 26, 52, 0.05)",
      minHeight: 78,
    }}
  >
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Box
        sx={{
          width: 16,
          height: 16,
          borderRadius: "999px",
          border: "1.5px solid #9aa7b7",
          flexShrink: 0,
          mt: 0.25,
        }}
      />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" sx={{ color: "#3a4b63", fontWeight: 900, lineHeight: 1.2 }}>
          {item.from} <Box component="span" sx={{ color: "#8a96a8", px: 0.5 }}>Â·</Box> {item.to}
        </Typography>
        <Typography variant="caption" sx={{ color: "#6a778a", fontWeight: 700, display: "block", mt: 0.3 }}>
          {item.date}
        </Typography>
      </Box>
    </Stack>
  </Paper>
);

const PopularCard = ({ item }) => (
  <Paper
    elevation={0}
    sx={{
      borderRadius: 4,
      border: "1px solid #e6ebf2",
      background: "#fff",
      boxShadow: "0 10px 24px rgba(8, 26, 52, 0.05)",
      px: 2.25,
      py: 2,
      minHeight: 154,
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      transition: "transform 160ms ease, box-shadow 160ms ease",
      "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: "0 16px 28px rgba(8, 26, 52, 0.08)",
      },
    }}
  >
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
      <Stack direction="row" spacing={1.4} alignItems="flex-start">
        <Box sx={{ display: "grid", justifyItems: "center", pt: 0.3 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: "999px", border: "1.5px solid #23314a" }} />
          <Box sx={{ width: 2, height: 25, background: "#23314a", my: 0.35, borderRadius: 999 }} />
          <Box sx={{ width: 8, height: 8, borderRadius: "999px", border: "1.5px solid #23314a" }} />
        </Box>

        <Box>
          <Typography variant="body1" sx={{ color: "#10233f", fontWeight: 900, lineHeight: 1.15 }}>
            {item.from}
          </Typography>
          <Typography variant="body1" sx={{ color: "#10233f", fontWeight: 900, lineHeight: 1.15, mt: 1 }}>
            {item.to}
          </Typography>
        </Box>
      </Stack>

      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: "999px",
          display: "grid",
          placeItems: "center",
          flexShrink: 0,
          background: "rgba(13, 122, 69, 0.1)",
          color: "#0d7a45",
        }}
      >
        <ArrowForwardRounded fontSize="small" />
      </Box>
    </Stack>

    <Stack direction="row" justifyContent="space-between" alignItems="end" sx={{ pt: 2 }}>
      <Box>
        <Typography variant="body2" sx={{ color: "#5d6a7c", fontWeight: 700 }}>
          {item.price}
        </Typography>
        <Typography variant="caption" sx={{ color: "#6a778a", fontWeight: 700, display: "block", mt: 0.5 }}>
          {item.duration}
        </Typography>
      </Box>
    </Stack>
  </Paper>
);

function formatTripDateTime(value, timezone) {
  if (!value) {
    return { dateLabel: "--", weekdayLabel: "--", timeLabel: "--:--" };
  }

  const date = moment(value).tz(timezone || "Africa/Douala");
  return {
    dateLabel: date.format("DD MMM YYYY"),
    weekdayLabel: date.format("dddd"),
    timeLabel: date.format("HH:mm"),
  };
}

function formatTripMoney(amount, currency = "FCFA") {
  const value = Number(amount);
  if (!Number.isFinite(value) || value <= 0) return `-- ${currency}`;
  return `${value.toLocaleString("fr-FR")} ${currency}`;
}

function shortId(value) {
  if (!value) return "--";
  const text = String(value);
  return text.length > 10 ? `${text.slice(0, 8)}...` : text;
}

function getTripStopCount(trip) {
  const explicitStops = trip?.stops_count ?? trip?.stop_count ?? trip?.number_of_stops ?? trip?.ride_waypoints?.length;
  const stops = Number(explicitStops);
  if (Number.isFinite(stops) && stops >= 0) return stops;
  return 0;
}

function safeJoin(...parts) {
  return parts.filter(Boolean).join(" ").trim();
}

function formatDurationFromRide(ride, fallback = "--") {
  if (!ride?.departure_at || !ride?.estimated_arrival_at) return fallback;
  const departure = new Date(ride.departure_at);
  const arrival = new Date(ride.estimated_arrival_at);
  if (Number.isNaN(departure.getTime()) || Number.isNaN(arrival.getTime())) return fallback;
  const durationMinutes = Math.max(0, Math.round((arrival.getTime() - departure.getTime()) / 60000));
  const hours = Math.floor(durationMinutes / 60);
  const minutes = String(durationMinutes % 60).padStart(2, "0");
  return `${hours}h${minutes}`;
}

function extractRideBookingRows(payload) {
  const candidates = [
    payload?.results,
    payload?.bookings,
    payload?.rides,
    payload?.data?.data?.data,
    payload?.data?.results,
    payload?.data?.bookings,
    payload?.data?.rides,
    payload?.data?.data,
    payload?.data?.data?.results,
    payload?.data?.data?.bookings,
    payload?.data?.data?.rides,
    payload?.data,
  ];

  const rows = candidates.find((value) => Array.isArray(value));
  if (Array.isArray(rows)) {
    return rows;
  }

  const nestedObject = candidates.find((value) => value && typeof value === "object" && !Array.isArray(value));
  if (nestedObject) {
    return extractRideBookingRows(nestedObject);
  }

  return [];
}

function normalizeTripDetail(trip, timezone, t) {
  const tf = t || ((id) => id);
  const source = trip?.raw || trip || {};
  const rideSource = source?.ride || source?.rides || source?.trip || {};
  const bookingSource = source?.booking || source?.bookings || {};
  const passengerSource = source?.passenger || source?.user || source?.users || {};
  const mergedSource = {
    ...rideSource,
    ...bookingSource,
    ...source,
    ride: rideSource,
    rides: source?.rides || rideSource,
    booking: bookingSource,
    users: source?.users || passengerSource,
    passenger: passengerSource,
    driver: source?.driver || rideSource?.driver || bookingSource?.driver || {},
  };

  const departure = formatTripDateTime(
    mergedSource?.departure_at || mergedSource?.rides?.departure_at || mergedSource?.ride?.departure_at,
    timezone
  );
  const arrival = formatTripDateTime(
    mergedSource?.estimated_arrival_at || mergedSource?.rides?.estimated_arrival_at || mergedSource?.ride?.estimated_arrival_at,
    timezone
  );
  const rideWaypoints = Array.isArray(mergedSource?.ride_waypoints)
    ? mergedSource.ride_waypoints
    : Array.isArray(mergedSource?.rides?.ride_waypoints)
      ? mergedSource.rides.ride_waypoints
      : Array.isArray(mergedSource?.ride?.ride_waypoints)
        ? mergedSource.ride.ride_waypoints
        : [];
  const driverName = safeJoin(
    mergedSource?.driver?.first_name,
    mergedSource?.driver?.last_name,
    mergedSource?.users?.first_name,
    mergedSource?.users?.last_name,
    mergedSource?.passenger?.first_name,
    mergedSource?.passenger?.last_name,
  ) || mergedSource?.driver_name || tf("course.normalize.driver");
  const passengerName = safeJoin(
    mergedSource?.users?.first_name,
    mergedSource?.users?.last_name,
    mergedSource?.passenger?.first_name,
    mergedSource?.passenger?.last_name,
  ) || mergedSource?.userFullName || mergedSource?.passenger_name || tf("course.normalize.passenger");
  const vehicleNo =
    mergedSource?.vehicle_no ||
    mergedSource?.rides?.vehicles?.vehicle_no ||
    mergedSource?.ride?.vehicles?.vehicle_no ||
    mergedSource?.vehicle?.vehicle_no ||
    mergedSource?.vehicleNumber ||
    mergedSource?.plate_number ||
    "--";
  const vehicleName =
    mergedSource?.vehicle_name ||
    mergedSource?.rides?.vehicles?.vehicle_name ||
    mergedSource?.ride?.vehicles?.vehicle_name ||
    mergedSource?.vehicle?.vehicle_name ||
    mergedSource?.vehicleModel ||
    mergedSource?.vehicle_model ||
    "--";
  const comfortClass =
    mergedSource?.comfort_class ||
    mergedSource?.rides?.comfort_class ||
    mergedSource?.ride?.comfort_class ||
    "--";
  const originLabel =
    mergedSource?.origin?.addressName ||
    mergedSource?.origin?.name ||
    mergedSource?.rides?.places_rides_origin_place_idToplaces?.name ||
    mergedSource?.ride?.places_rides_origin_place_idToplaces?.name ||
    mergedSource?.origin_place_name ||
    mergedSource?.origin_place_id ||
      tf("course.normalize.departure");
  const destinationLabel =
    mergedSource?.destination?.addressName ||
    mergedSource?.destination?.name ||
    mergedSource?.rides?.places_rides_destination_place_idToplaces?.name ||
    mergedSource?.ride?.places_rides_destination_place_idToplaces?.name ||
    mergedSource?.destination_place_name ||
    mergedSource?.destination_place_id ||
      tf("course.normalize.arrival");
  const stopCount = getTripStopCount(mergedSource);
  const waypoints = rideWaypoints
    .map((wp, index) => ({
      id: wp?.id || wp?._id || `${index}-${wp?.sequence_order || index + 1}`,
      name: wp?.places?.name || wp?.place?.name || wp?.place_name || wp?.name || tf("course.normalize.stop", { n: index + 1 }),
      sequence: Number(wp?.sequence_order ?? index + 1) || index + 1,
      offsetLabel: Number.isFinite(Number(wp?.arrival_offset_min)) ? `${Number(wp.arrival_offset_min)} min` : "--",
    }))
    .sort((a, b) => a.sequence - b.sequence);

  const bookingStatus = String(mergedSource?.status || mergedSource?.booking?.status || "pending").toLowerCase();
  const statusLabel = bookingStatus === "confirmed"
    ? tf("course.normalize.confirmed")
    : bookingStatus === "cancelled" || bookingStatus === "canceled"
      ? tf("course.normalize.refused")
      : bookingStatus === "completed"
        ? tf("course.normalize.completed")
        : tf("course.normalize.pending");
  const statusTone = bookingStatus === "completed" || bookingStatus === "cancelled" || bookingStatus === "canceled"
    ? "is-past"
    : "is-active";

  return {
    raw: mergedSource,
    id: mergedSource?.id || mergedSource?.booking?.id || mergedSource?.ride_id || mergedSource?.ride?.id || "-",
    rideId: mergedSource?.ride_id || mergedSource?.ride?.id || mergedSource?.id || "-",
    journeyTitle: `${originLabel} â†’ ${destinationLabel}`,
    originLabel,
    destinationLabel,
    departureTime: departure.timeLabel,
    arrivalTime: arrival.timeLabel,
    departureDateLabel: `${departure.weekdayLabel} ${departure.dateLabel}`,
    tripDuration: formatDurationFromRide(source),
    stopCount,
    waypoints,
    driverName,
    passengerName,
    vehicleNo,
    vehicleName,
    comfortClass,
    modeLabel: source?.type === "train" ? tf("course.normalize.train") : source?.type === "bus" ? tf("course.normalize.bus") : tf("course.normalize.carpooling"),
    statusLabel,
    statusTone,
    availableSeats: Number(mergedSource?.available_seats ?? mergedSource?.ride?.available_seats ?? 0),
    totalSeats: Number(mergedSource?.total_seats ?? mergedSource?.ride?.total_seats ?? 0),
    bookedSeats: Number(mergedSource?.seats_booked ?? mergedSource?.seatsBooked ?? mergedSource?.booking?.seats_booked ?? 0),
    totalAmount: Number(mergedSource?.total_amount ?? mergedSource?.totalAmount ?? mergedSource?.booking?.total_amount ?? 0),
    pricePerSeat: Number(mergedSource?.price_per_seat ?? mergedSource?.ride?.price_per_seat ?? 0),
    serviceFee: Number(mergedSource?.service_fee ?? mergedSource?.booking?.service_fee ?? 0),
    currency: mergedSource?.currency || mergedSource?.booking?.currency || "FCFA",
    phone: mergedSource?.users?.phone || mergedSource?.passenger?.phone || mergedSource?.phone || mergedSource?.contact_phone || mergedSource?.user_phone || "--",
  };
}

function normalizeBookingDetail(booking, timezone, t) {
  const tf = t || ((id) => id);
  const source = booking?.raw || booking || {};
  const ride = source?.rides || source?.ride || source?.ride_details || {};
  const passenger = source?.users || source?.user || source?.passenger || {};
  const driver = ride?.driver || source?.driver || {};
  const origin = source?.origin || ride?.origin_place || ride?.places_rides_origin_place_idToplaces || {};
  const destination =
    source?.destination || ride?.destination_place || ride?.places_rides_destination_place_idToplaces || {};
  const departure = formatTripDateTime(ride?.departure_at || source?.departure_at || source?.created_at, timezone);
  const arrival = formatTripDateTime(ride?.estimated_arrival_at || source?.estimated_arrival_at, timezone);
  const seatsBooked = Number(source?.seats_booked ?? source?.seatsBooked ?? 0);
  const totalAmount = Number(source?.total_amount ?? source?.totalAmount ?? 0);
  const pricePerSeat = Number(source?.price_per_seat ?? ride?.price_per_seat ?? 0);

  return {
    raw: source,
    id: source?.id || source?.booking_id || "-",
    bookingId: source?.id || source?.booking_id || "-",
    rideId: ride?.id || source?.ride_id || "-",
    bookingNumber: source?.booking_number || String(source?.id || source?.booking_id || "-").slice(0, 23),
    ticketNumber: String(source?.id || source?.booking_id || "-").slice(0, 12).toUpperCase(),
    passengerName:
      safeJoin(passenger?.first_name, passenger?.last_name) ||
      safeJoin(passenger?.firstName, passenger?.lastName) ||
      "--",
    passengerEmail: passenger?.email || source?.email || "--",
    passengerPhone: passenger?.phone || source?.phone || "--",
    driverName:
      safeJoin(driver?.first_name, driver?.last_name) ||
      safeJoin(driver?.firstName, driver?.lastName) ||
      "--",
    vehicleNo:
      ride?.vehicle_no ||
      ride?.vehicles?.vehicle_no ||
      ride?.vehicle?.vehicle_no ||
      source?.vehicle_no ||
      source?.vehicleNumber ||
      "--",
    vehicleName:
      ride?.vehicle_name ||
      ride?.vehicles?.vehicle_name ||
      ride?.vehicle?.vehicle_name ||
      source?.vehicle_name ||
      source?.vehicle_model ||
      "--",
    rideType: source?.ride_type || ride?.ride_type || "--",
    comfortClass: source?.comfort_class || ride?.comfort_class || "--",
    departureDateLabel: `${departure.weekdayLabel} ${departure.dateLabel}`,
    departureTime: departure.timeLabel,
    arrivalTime: arrival.timeLabel,
    originLabel: origin?.name || origin?.addressName || ride?.origin_place_name || ride?.origin_place_id || "--",
    destinationLabel:
      destination?.name || destination?.addressName || ride?.destination_place_name || ride?.destination_place_id || "--",
    seatsBooked: Number.isFinite(seatsBooked) && seatsBooked > 0 ? seatsBooked : 1,
    pricePerSeat: Number.isFinite(pricePerSeat) && pricePerSeat > 0 ? pricePerSeat : 0,
    totalAmount: Number.isFinite(totalAmount) && totalAmount > 0 ? totalAmount : 0,
    serviceFee: Number(source?.service_fee ?? 0),
    currency: source?.currency || ride?.currency || "XAF",
    status: String(source?.status || "pending").toLowerCase(),
    statusLabel:
      String(source?.status || "pending").toLowerCase() === "confirmed"
        ? tf("course.normalize.confirmed")
        : String(source?.status || "pending").toLowerCase() === "completed"
          ? tf("course.normalize.completed")
          : String(source?.status || "pending").toLowerCase() === "cancelled" ||
              String(source?.status || "pending").toLowerCase() === "canceled"
            ? tf("course.normalize.cancelled")
            : tf("course.normalize.pending"),
    statusTone:
      String(source?.status || "pending").toLowerCase() === "confirmed" ||
      String(source?.status || "pending").toLowerCase() === "pending"
        ? "is-active"
        : "is-past",
    availableSeats: Number(ride?.available_seats ?? 0),
    totalSeats: Number(ride?.total_seats ?? 0),
    phone: passenger?.phone || source?.phone || "--",
  };
}

const TripRowCard = ({ trip, timezone, onOpenDetails, onConfirmCourse }) => {
  const { formatMessage } = useIntl();
  const t = (id, values) => formatMessage({ id }, values);
  const details = normalizeTripDetail(trip, timezone, t);

  return (
    <Paper
      elevation={0}
      role="button"
      tabIndex={0}
      onClick={() => onOpenDetails?.(trip)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpenDetails?.(trip);
        }
      }}
      sx={{
        borderRadius: 4,
        border: "1px solid rgba(15, 39, 72, 0.08)",
        background: "#fff",
        boxShadow: "0 10px 24px rgba(8, 26, 52, 0.05)",
        px: { xs: 1.75, md: 2.25 },
        py: { xs: 1.6, md: 1.8 },
        cursor: "pointer",
        transition: "transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease",
        "&:hover": {
          transform: "translateY(-1px)",
          boxShadow: "0 14px 28px rgba(8, 26, 52, 0.08)",
          borderColor: "rgba(15, 39, 72, 0.14)",
        },
        "&:focus-visible": {
          outline: "2px solid #0d7a45",
          outlineOffset: 2,
        },
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", md: "center" }}
        justifyContent="space-between"
        sx={{ width: "100%" }}
      >
        <Box
          sx={{
            flex: { xs: "1 1 auto", md: "1.05 1 0" },
            display: "grid",
            gap: 1.1,
            pt: 0.5,
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
            <Box sx={{ minWidth: 56 }}>
              <Typography variant="body2" sx={{ color: "#0f2a4a", fontWeight: 500, lineHeight: 1.1 }}>
                {details.departureTime}
              </Typography>
              <Typography variant="caption" sx={{ color: "#6a778a", fontWeight: 500 }}>
                {details.departureDateLabel}
              </Typography>
            </Box>

            <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: "11px",
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
                background: "rgba(13, 122, 69, 0.1)",
                color: "#0d7a45",
              }}
            >
              <PersonOutlineOutlined fontSize="small" />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ color: "#10233f", fontWeight: 900, lineHeight: 1.05, textTransform: "lowercase" }}>
                {details.passengerName}
              </Typography>
              <Typography variant="caption" sx={{ color: "#6a778a", fontWeight: 700, display: "block", mt: 0.25 }}>
                {details.phone}
              </Typography>
            </Box>
          </Stack>
          </Stack>

          

          
        </Box>
          <Box
          sx={{
            minWidth: 0,
            flex: { xs: "1 1 auto", md: "1.8 1 0" },
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "auto auto auto auto" },
            columnGap: { xs: 1.5, md: 1.75 },
            rowGap: { xs: 1.5, md: 0 },
            alignItems: "center",
          }}
        >
          <Stack direction="column" spacing={0.35} alignItems="flex-start" sx={{ minWidth: 0 }}>
            <Chip
              label={details.modeLabel}
              size="small"
              sx={{
                borderRadius: "999px",
                background: "#eff8f1",
                color: "#0d7a45",
                fontWeight: 800,
                width: "fit-content",
              }}
            />
            <Chip
              label={details.comfortClass}
              size="small"
              sx={{
                borderRadius: "999px",
                background: "#f4f7fb",
                color: "#334155",
                fontWeight: 800,
                width: "fit-content",
              }}
            />
             <Chip
              label={details.statusLabel}
              size="small"
              sx={{
                borderRadius: "999px",
                background: "#f4f7fb",
                color: "#334155",
                fontWeight: 800,
                width: "fit-content",
              }}
            />
          </Stack>

          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: "11px",
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
                background: "rgba(13, 122, 69, 0.1)",
                color: "#0d7a45",
              }}
            >
              <DirectionsCarOutlined fontSize="small" />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ color: "#10233f", fontWeight: 900, lineHeight: 1.05 }}>
                {details.vehicleNo}
              </Typography>
              <Typography variant="caption" sx={{ color: "#6a778a", fontWeight: 700, display: "block", mt: 0.25 }}>
                {details.vehicleName}
              </Typography>
            </Box>
          </Stack>

          <Box
            sx={{
              minWidth: 0,
              display: "grid",
              gridTemplateColumns: "14px minmax(0, 1fr)",
              gap: 1,
              alignItems: "start",
            }}
          >
            <Box
              sx={{
                position: "relative",
                width: 14,
                height: 38,
                display: "grid",
                justifyItems: "center",
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: 7,
                  bottom: 7,
                  width: 2,
                  borderRadius: 999,
                  background: "#0d7a45",
                }}
              />
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "999px",
                  border: "2px solid #0d7a45",
                  background: "#fff",
                  position: "absolute",
                  top: 0,
                }}
              />
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "999px",
                  border: "2px solid #0d7a45",
                  background: "#fff",
                  position: "absolute",
                  bottom: 0,
                }}
              />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ color: "#10233f", fontWeight: 900, lineHeight: 1.05 }}>
                {details.originLabel}
              </Typography>
              <Typography variant="body2" sx={{ color: "#10233f", fontWeight: 900, lineHeight: 1.05, mt: 0.9 }}>
                {details.destinationLabel}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: "10px",
                  display: "grid",
                  placeItems: "center",
                  flexShrink: 0,
                  background: "rgba(13, 122, 69, 0.1)",
                  color: "#0d7a45",
                }}
              >
                <EventSeatOutlined fontSize="small" />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ color: "#6a778a", fontWeight: 700, display: "block", mt: 0.25 }}>
                  {t("course.trip.seatsBooked", { count: details.bookedSeats })}
                </Typography>
              </Box>
            </Stack>
          </Box>
        </Box> 

        <Stack direction="row" spacing={1.5} alignItems="center" flexShrink={0}>
          <Box sx={{ textAlign: "right" }}>
            <Typography variant="h6" sx={{ color: "#0d7a45", fontWeight: 900, lineHeight: 1.05 }}>
              {formatTripMoney(details.totalAmount, details.currency)}
            </Typography>
            <Typography variant="caption" sx={{ color: "#6a778a", fontWeight: 700 }}>
              {t("course.trip.perSeat")}
            </Typography>
          </Box>

          <Button
            variant="outlined"
            onClick={(event) => {
              event.stopPropagation();
              onConfirmCourse?.(trip);
            }}
            sx={{
              borderRadius: 3,
              textTransform: "none",
              fontWeight: 900,
              minWidth: 132,
              borderColor: "#b8dbc3",
              color: "#0d7a45",
              background: "#fff",
              "&:hover": {
                borderColor: "#94c9a4",
                background: "#fbfefb",
              },
            }}
          >
            {t("course.trip.confirmCourse")} 
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
};

const CourseResultsTable = ({ rows, timezone, onOpenDetails, onConfirmCourse, loading }) => {
  const { formatMessage } = useIntl();
  const t = (id, values) => formatMessage({ id }, values);
  const [pageSize, setPageSize] = useState(5);

  const renderTextCell = useCallback(
    (value) => (
      <Typography variant="body2" noWrap title={value ?? "--"} sx={{ width: "100%" }}>
        {value ?? "--"}
      </Typography>
    ),
    []
  );

  const normalizedRows = useMemo(
    () =>
      rows.map((trip, index) => ({
        ...normalizeTripDetail(trip, timezone, t),
        sno: index + 1,
      })),
    [rows, timezone, t]
  );

  const columns = useMemo(
    () => [
      {
        field: "sno",
        headerName: t("course.table.sno"),
        minWidth: 70,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => renderTextCell(params?.row?.sno),
      },
     
      {
        field: "route",
        headerName: t("course.table.route"),
        minWidth: 240,
        flex: 1.3,
        sortable: true,
        renderCell: (params) => renderTextCell(`${params?.row?.originLabel || "--"} → ${params?.row?.destinationLabel || "--"}`),
      },
      {
        field: "passenger",
        headerName: t("course.table.passenger"),
        minWidth: 180,
        flex: 1,
        sortable: true,
        renderCell: (params) => (
          <Box sx={{ display: "grid", gap: 0.15, minWidth: 0 }}>
            <Typography variant="body2" noWrap title={params?.row?.passengerName || "--"} sx={{ fontWeight: 800, color: "#10233f", lineHeight: 1.15 }}>
              {params?.row?.passengerName || "--"}
            </Typography>
            <Typography variant="caption" noWrap title={params?.row?.phone || "--"} sx={{ color: "#64748b", fontWeight: 600, lineHeight: 1.15 }}>
              {params?.row?.phone || "--"}
            </Typography>
          </Box>
        ),
      },
      {
        field: "departure",
        headerName: t("course.table.departure"),
        minWidth: 150,
        flex: 1,
        sortable: true,
        renderCell: (params) =>
          renderTextCell(`${params?.row?.departureDateLabel || "--"} ${params?.row?.departureTime || "--:--"}`),
      },
      {
        field: "vehicle",
        headerName: t("course.table.vehicle"),
        minWidth: 170,
        flex: 1,
        sortable: true,
        renderCell: (params) => renderTextCell(`${params?.row?.vehicleNo || "--"}${params?.row?.vehicleName ? ` - ${params.row.vehicleName}` : ""}`),
      },
      {
        field: "status",
        headerName: t("course.table.status"),
        minWidth: 140,
        flex: 0.9,
        sortable: true,
        renderCell: (params) => (
          <span className={`tc-status-pill tc-status-pill--${params?.row?.statusTone || "is-active"}`}>
            {params?.row?.statusLabel || "--"}
          </span>
        ),
      },
      {
        field: "seats",
        headerName: t("course.table.seats"),
        minWidth: 110,
        flex: 0.75,
        sortable: true,
        renderCell: (params) => renderTextCell(`${params?.row?.bookedSeats || 0}/${params?.row?.totalSeats || "--"}`),
      },
      {
        field: "amount",
        headerName: t("course.table.total"),
        minWidth: 130,
        flex: 0.9,
        sortable: true,
        renderCell: (params) => renderTextCell(formatTripMoney(params?.row?.totalAmount, params?.row?.currency)),
      },
      {
        field: "detailsAction",
        headerName: t("course.table.detail"),
        minWidth: 150,
        flex: 0.95,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <Button
            variant="outlined"
            onClick={(event) => {
              event.stopPropagation();
              onOpenDetails?.(params?.row);
            }}
            sx={{
              minWidth: 112,
              height: 34,
              px: 1.8,
              borderRadius: "10px",
              borderColor: "#a8dcb8",
              background: "#ffffff",
              color: "#0b7a45",
              textTransform: "none",
              fontWeight: 800,
              fontSize: 13,
              boxShadow: "none",
              lineHeight: 1,
              "&:hover": {
                borderColor: "#7dcca0",
                background: "#f6fbf7",
                boxShadow: "none",
              },
            }}
          >
            {t("course.table.viewDetail")}
          </Button>
        ),
      },
      {
        field: "confirmAction",
        headerName: "CONFIRM",
        minWidth: 170,
        flex: 1,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <Button
            variant="outlined"
            onClick={(event) => {
              event.stopPropagation();
              onConfirmCourse?.(params?.row);
            }}
            sx={{
              minWidth: 132,
              height: 34,
              px: 1.8,
              borderRadius: "10px",
              borderColor: "#ff9800",
              background: "#ffffff",
              color: "#ff9800",
              textTransform: "none",
              fontWeight: 800,
              fontSize: 13,
              boxShadow: "none",
              lineHeight: 1,
              "&:hover": {
                borderColor: "#ef8b00",
                background: "#fff8ef",
                boxShadow: "none",
              },
            }}
          >
            {t("course.trip.confirmCourse")}
          </Button>
        ),
      },
    ],
    [onConfirmCourse, onOpenDetails, renderTextCell, t]
  );

  return (
    <Paper
      elevation={0}
      className="tc-table-card"
      sx={{
        borderRadius: 4,
        border: "1px solid rgba(15, 39, 72, 0.08)",
        background: "#fff",
        overflow: "hidden",
      }}
    >
      <DataGrid
        autoHeight
        rows={normalizedRows}
        columns={columns}
        loading={loading}
        disableSelectionOnClick
        disableColumnMenu
        density="compact"
        pagination
        pageSize={pageSize}
        onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
        rowsPerPageOptions={[5, 10, 25, 50]}
        rowHeight={72}
        headerHeight={56}
        getRowId={(row) => row.id || row.bookingId || row.rideId || row.sno}
        localeText={{ noRowsLabel: "No results found." }}
        className="tc-data-grid-table"
        onRowClick={(params) => onOpenDetails?.(params?.row)}
        sx={{
          "& .MuiDataGrid-row": {
            cursor: "pointer",
          },
        }}
      />
    </Paper>
  );
};

void TripRowCard;

function ClockMiniIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" aria-hidden="true">
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5v5l3 2" />
    </svg>
  );
}

const DetailRow = ({ label, value }) => (
  <Box sx={{ display: "grid", gap: 0.35 }}>
    <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, letterSpacing: "0.04em" }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ color: "#10233f", fontWeight: 700, wordBreak: "break-word" }}>
      {value || "--"}
    </Typography>
  </Box>
);

const RideDetailsModal = ({ open, ride, loading, error, onClose, passengerCount, bookingSeats, onBookingSeatsChange, onReserveNow, reserveLoading }) => {
  const { formatMessage } = useIntl();
  const t = (id, values) => formatMessage({ id }, values);

  return (
  <Dialog
    open={open}
    onClose={onClose}
    fullWidth
    maxWidth="xl"
    PaperProps={{ sx: { borderRadius: 4, overflow: "hidden" } }}
  >
    <DialogTitle sx={{ p: 0, borderBottom: "1px solid #e6ebf2", background: "#fff" }}>
      <Box sx={{ px: { xs: 2, md: 3 }, py: 2 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          spacing={2}
          alignItems={{ xs: "flex-start", sm: "center" }}
        >
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 900, color: "#10233f", lineHeight: 1.1 }}>
              {t("course.ride.title")}
            </Typography>
            
          </Box>

          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Chip
              label={ride?.statusLabel || "--"}
              sx={{
                height: 30,
                borderRadius: "999px",
                background: ride?.statusTone === "is-past" ? "#f2f5f9" : "#ecf8ee",
                color: ride?.statusTone === "is-past" ? "#6c7787" : "#0b7a45",
                fontWeight: 800,
                "& .MuiChip-label": { px: 1.2 },
              }}
            />
            <Button variant="outlined" onClick={onClose} sx={{ textTransform: "none", fontWeight: 900, borderRadius: 2.5 }}>
              {t("course.auth.close")}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </DialogTitle>

    <DialogContent sx={{ p: 0, background: "#f3f6fb" }}>
      {loading ? (
        <Box sx={{ minHeight: 420, display: "grid", placeItems: "center", gap: 2, p: 4 }}>
          <CircularProgress />
          <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 700 }}>
            {t("course.ride.loading")}
          </Typography>
        </Box>
      ) : error ? (
        <Box sx={{ p: 3 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      ) : ride ? (
        <Box sx={{ p: { xs: 2, md: 2.5 } }} className="ride-details-main">
          <div className="ride-details-grid">
            <div className="ride-details-left">
              <article className="ride-itinerary-card">
                <div className="ride-summary-timeline">
                  <div className="ride-summary-row">
                    <div className="ride-summary-time">
                      <strong>{ride.departureTime || "--:--"}</strong>
                      <span>{ride.tripDuration || "--"}</span>
                    </div>
                    <div className="ride-summary-track" aria-hidden="true">
                      <span className="ride-summary-dot" />
                      <span className="ride-summary-line" />
                      <span className="ride-summary-dot is-end" />
                    </div>
                    <div className="ride-summary-stop">
                      <strong>{ride.originLabel}</strong>
                      <span>{t("course.ride.departurePoint")}</span>
                    </div>
                  </div>

                  <div className="ride-summary-stop-label">
                    <ClockMiniIcon />
                    <span>
                      {ride.tripDuration || "--"} Â· {t("course.ride.stops", { count: ride.stopCount ?? 0 })}
                    </span>
                  </div>

                  {ride.waypoints.length ? (
                    <Box sx={{ pl: "80px", display: "grid", gap: 1.1, mt: 0.5 }}>
                      {ride.waypoints.map((waypoint) => (
                        <Box
                          key={waypoint.id}
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "12px minmax(0, 1fr)",
                            gap: 1,
                            alignItems: "start",
                          }}
                        >
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: "999px",
                              border: "2px solid #1b273f",
                              background: "#fff",
                              mt: 0.6,
                            }}
                          />
                          <Box>
                            <Typography variant="body2" sx={{ color: "#10233f", fontWeight: 800 }}>
                              {waypoint.name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>
                              SÃ©quence {waypoint.sequence} Â· Offset {waypoint.offsetLabel}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  ) : null}

                  <div className="ride-summary-row is-arrival">
                    <div className="ride-summary-time">
                      <strong>{ride.arrivalTime || "--:--"}</strong>
                      <span>{t("course.ride.estimatedArrival")}</span>
                    </div>
                    <div className="ride-summary-track" aria-hidden="true">
                      <span className="ride-summary-dot" />
                      <span className="ride-summary-line" />
                      <span className="ride-summary-dot is-end" />
                    </div>
                    <div className="ride-summary-stop">
                      <strong>{ride.destinationLabel}</strong>
                      <span>{t("course.ride.arrivalPoint")}</span>
                    </div>
                  </div>
                </div>

              
              </article>

              <Paper
                elevation={0}
                sx={{
                  borderRadius: 4,
                  border: "1px solid #e6ebf2",
                  background: "#fff",
                  p: 2,
                  boxShadow: "0 10px 24px rgba(8, 26, 52, 0.05)",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1.5,
                    alignItems: "stretch",
                  }}
                >
                  <Box
                    sx={{
                      flex: "1 1 190px",
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 0.8,
                      alignContent: "start",
                      minWidth: 0,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.8,
                        flexWrap: "wrap",
                      }}
                    >
                      <Chip
                        label={ride.modeLabel || t("course.normalize.carpooling")}
                        size="small"
                        sx={{
                          width: "fit-content",
                          height: 24,
                          borderRadius: "999px",
                          background: "#edf8ec",
                          color: "#0b7a45",
                          fontWeight: 900,
                          "& .MuiChip-label": { px: 1.1 },
                        }}
                      />
                      <Chip
                        label={ride.comfortClass || "standard"}
                        size="small"
                        sx={{
                          width: "fit-content",
                          height: 24,
                          borderRadius: "999px",
                          background: "#edf8ec",
                          color: "grey",
                          fontWeight: 900,
                          "& .MuiChip-label": { px: 1.1 },
                        }}
                      />
                    </Box>
                  </Box>

                  <Box sx={{ flex: "1 1 190px", display: "flex", alignItems: "center", gap: 1.2, minWidth: 0 }}>
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: "999px",
                        background: "#edf8ec",
                        color: "#0b7a45",
                        display: "grid",
                        placeItems: "center",
                        flexShrink: 0,
                      }}
                    >
                      <DirectionsCarOutlined fontSize="small" />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ color: "#10233f", fontWeight: 900, lineHeight: 1.2 }}>
                        {ride.vehicleNo || "--"}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700 }}>
                        {ride.vehicleName || "--"}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ flex: "1 1 190px", display: "flex", alignItems: "center", gap: 1.2, minWidth: 0 }}>
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: "999px",
                        background: "#edf8ec",
                        color: "#0b7a45",
                        display: "grid",
                        placeItems: "center",
                        flexShrink: 0,
                      }}
                    >
                      <EventSeatOutlined fontSize="small" />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ color: "#10233f", fontWeight: 900, lineHeight: 1.2 }}>
                        {t("course.ride.seatsAvailable")}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700 }}>
                        {t("course.ride.seatsCount", { available: ride.availableSeats, total: ride.totalSeats || "--" })}
                      </Typography>
                    </Box>
                  </Box>

                </Box>

                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 4,
                    border: "1px solid #e6ebf2",
                    background: "#fff",
                    p: 1.25,
                    boxShadow: "0 10px 24px rgba(8, 26, 52, 0.05)",
                    marginTop : "5px",
                  }}
                >
                  <Typography variant="h6" sx={{ color: "#10233f", fontWeight: 900, mb: 1.5 }}>
                    {t("course.ride.driver")}
                  </Typography>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                   <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, minWidth: 0 }}>
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: "999px",
                        background: "#edf8ec",
                        color: "#0b7a45",
                        display: "grid",
                        placeItems: "center",
                        flexShrink: 0,
                      }}
                    >
                      <PersonOutlineOutlined fontSize="small" />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ color: "#10233f", fontWeight: 900, lineHeight: 1.2 }}>
                        {ride.driverName || "--"}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700 }}>
                        {ride.phone || "--"}
                      </Typography>
                    </Box>
                  </Box>
                  </Stack>
                </Paper>
              </Paper>

            </div>
            

            <aside className="ride-summary-panel">
            

              <div className="ride-booking-card">
                <div className="ride-availability">
                  <ClockMiniIcon />
                  <span>{t("course.ride.placesAvailable", { count: ride.availableSeats })}</span>
                </div>

                <Box sx={{ mt: 1.5, display: "grid", gap: 0.8 }}>
                  <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 800 }}>
                    {t("course.ride.seatsToBook")}
                  </Typography>
                  <Select
                    size="small"
                    value={bookingSeats}
                    onChange={(event) => onBookingSeatsChange(Number(event.target.value))}
                    disabled={!ride.availableSeats}
                    sx={{
                      borderRadius: 2.5,
                      background: "#fff",
                      "& .MuiSelect-select": {
                        py: 1,
                        fontWeight: 800,
                        color: "#10233f",
                      },
                    }}
                  >
                    {Array.from({ length: Math.max(1, Number(ride.availableSeats || 1)) }, (_, index) => index + 1).map((seatCount) => (
                      <MenuItem key={seatCount} value={seatCount}>
                        {seatCount}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>

                <div className="ride-price-row">
                  <div className="ride-passenger-count">
                  </div>
                  <div className="ride-price-block">
                    <strong>{formatTripMoney(ride.totalAmount, ride.currency)}</strong>
                    <span>{t("course.ride.total")}</span>
                  </div>
                </div>

                <button type="button" className="ride-book-now-btn" onClick={onReserveNow} disabled={reserveLoading || !ride.availableSeats}>
                  {reserveLoading ? t("course.ride.waiting") : t("course.ride.reserveNow")}
                </button>
              </div>
            </aside>
          </div>
        </Box>
      ) : null}
    </DialogContent>
  </Dialog>
  );
};

const BookingDetailsModal = ({
  open,
  booking,
  loading,
  error,
  onClose,
  onConfirm,
  onReject,
  confirmLoading,
  rejectLoading,
}) => {
  const { formatMessage } = useIntl();
  const t = (id, values) => formatMessage({ id }, values);
  const totalToPay =
    Number(booking?.totalAmount ?? 0) ||
    Number(booking?.pricePerSeat ?? 0) * Number(booking?.seatsBooked ?? 1) ||
    0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 4, overflow: "hidden", background: "#f3f6fb" } }}
    >
      <DialogTitle sx={{ p: 0, borderBottom: "1px solid #e6ebf2", background: "#fff" }}>
        <Box sx={{ px: { xs: 2, md: 3 }, py: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
            <Typography variant="h6" sx={{ fontWeight: 900, color: "#10233f" }}>
              {t("course.booking.title")}
            </Typography>
            <Button
              onClick={onClose}
              variant="text"
              sx={{
                minWidth: 36,
                width: 36,
                height: 36,
                borderRadius: "999px",
                color: "#64748b",
              }}
            >
              <CloseRounded fontSize="small" />
            </Button>
          </Stack>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 2, md: 2.25 }, background: "#f3f6fb" }}>
        {loading ? (
          <Box sx={{ minHeight: 420, display: "grid", placeItems: "center" }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : booking ? (
          <Box sx={{ display: "grid", gap: 1.5 }}>
            <Paper elevation={0} sx={{ borderRadius: 4, border: "1px solid #e6ebf2", background: "#fff", p: 2 }}>
              <Typography variant="subtitle2" sx={{ color: "#10233f", fontWeight: 900, mb: 1.2 }}>
                {t("course.booking.tripInfo")}
              </Typography>
              <Box
                sx={{
                  border: "1px solid #e6ebf2",
                  borderRadius: 3,
                  background: "#fff",
                  px: { xs: 1.5, sm: 2 },
                  py: { xs: 1.5, sm: 1.75 },
                }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1.05fr 1fr 1fr" },
                    gap: { xs: 1.5, sm: 2.25 },
                    alignItems: "center",
                  }}
                >
                  <Stack spacing={0.85} sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: "999px",
                          background: "#edf8ec",
                          color: "#0b7a45",
                          display: "grid",
                          placeItems: "center",
                          flexShrink: 0,
                        }}
                      >
                        <CalendarIcon />
                      </Box>
                      <Typography variant="body2" sx={{ color: "#10233f", fontWeight: 900, lineHeight: 1.15 }}>
                        {booking.departureDateLabel || "--"}
                      </Typography>
                    </Stack>

                    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: "999px",
                          background: "#edf8ec",
                          color: "#0b7a45",
                          display: "grid",
                          placeItems: "center",
                          flexShrink: 0,
                        }}
                      >
                        <ClockMiniIcon />
                      </Box>
                      <Typography variant="body2" sx={{ color: "#10233f", fontWeight: 900, lineHeight: 1.15 }}>
                        {booking.departureTime || "--:--"}
                      </Typography>
                    </Stack>
                  </Stack>

                  <Stack direction="row" spacing={1.8} alignItems="center" sx={{ minWidth: 0, justifySelf: { sm: "center" } }}>
                    <Box
                      sx={{
                        position: "relative",
                        width: 14,
                        height: 42,
                        flexShrink: 0,
                      }}
                    >
                      <Box
                        sx={{
                          position: "absolute",
                          left: "50%",
                          top: 6,
                          bottom: 6,
                          width: 2,
                          transform: "translateX(-50%)",
                          borderRadius: 999,
                          background: "#0b7a45",
                        }}
                      />
                      <Box
                        sx={{
                          position: "absolute",
                          left: "50%",
                          top: 0,
                          width: 8,
                          height: 8,
                          transform: "translateX(-50%)",
                          borderRadius: "999px",
                          border: "2px solid #0b7a45",
                          background: "#fff",
                        }}
                      />
                      <Box
                        sx={{
                          position: "absolute",
                          left: "50%",
                          bottom: 0,
                          width: 8,
                          height: 8,
                          transform: "translateX(-50%)",
                          borderRadius: "999px",
                          border: "2px solid #0b7a45",
                          background: "#fff",
                        }}
                      />
                    </Box>
                    <Typography
                      variant="body1"
                      sx={{
                        color: "#10233f",
                        fontWeight: 700,
                        fontSize: { xs: "1rem", sm: "1rem" },
                        lineHeight: 1.1,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {booking.originLabel || "--"}
                      <Box component="span" sx={{ color: "#9aa4b2", fontWeight: 700, mx: 1 }}>
                        →
                      </Box>
                      {booking.destinationLabel || "--"}
                    </Typography>
                  </Stack>

                  <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0, justifySelf: { sm: "end" } }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: "999px",
                        background: "#edf8ec",
                        color: "#0b7a45",
                        display: "grid",
                        placeItems: "center",
                        flexShrink: 0,
                      }}
                    >
                      <DirectionsCarOutlined fontSize="small" />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ color: "#10233f", fontWeight: 900, lineHeight: 1.1 }}>
                        {booking.vehicleNo || "--"}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700, display: "block", mt: 0.25 }}>
                        {booking.vehicleName || "--"}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              </Box>
            </Paper>

            <Paper elevation={0} sx={{ borderRadius: 4, border: "1px solid #e6ebf2", background: "#fff", p: 2 }}>
              <Typography variant="subtitle2" sx={{ color: "#10233f", fontWeight: 900, mb: 1.2 }}>
                {t("course.booking.passenger")}
              </Typography>
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ flexWrap: "wrap" }}>
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "999px",
                      background: "#edf8ec",
                      color: "#0b7a45",
                      display: "grid",
                      placeItems: "center",
                    }}
                  >
                    <PersonOutlineOutlined fontSize="small" />
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ color: "#10233f", fontWeight: 900 }}>
                      {booking.passengerName || "--"}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700, display: "block" }}>
                      {booking.passengerEmail || "--"}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700, display: "block" }}>
                      {booking.passengerPhone || "--"}
                    </Typography>
                  </Box>
                </Stack>
              </Stack>
            </Paper>

            <Paper elevation={0} sx={{ borderRadius: 4, border: "1px solid #e6ebf2", background: "#fff", p: 2 }}>
              <Typography variant="subtitle2" sx={{ color: "#10233f", fontWeight: 900, mb: 1.2 }}>
                {t("course.booking.title")}
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" },
                  gap: 1.25,
                }}
              >
                <Box>
                  <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700 }}>
                    {t("course.booking.seatsReserved")}
                  </Typography>
                  <Typography variant="h6" sx={{ color: "#10233f", fontWeight: 900 }}>
                    {booking.seatsBooked || 1}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700 }}>
                    {t("course.booking.pricePerSeat")}
                  </Typography>
                  <Typography variant="h6" sx={{ color: "#10233f", fontWeight: 900 }}>
                    {formatTripMoney(booking.pricePerSeat, booking.currency)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700 }}>
                    {t("course.booking.totalToPay")}
                  </Typography>
                  <Typography variant="h6" sx={{ color: "#10233f", fontWeight: 900 }}>
                    {formatTripMoney(totalToPay, booking.currency)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700 }}>
                    {t("course.booking.status")}
                  </Typography>
                  <Chip
                    label={booking.statusLabel || "--"}
                    size="small"
                    sx={{
                      mt: 0.5,
                      width: "fit-content",
                      borderRadius: "999px",
                      background: booking.statusTone === "is-past" ? "#eef2f7" : "#edf8ec",
                      color: booking.statusTone === "is-past" ? "#64748b" : "#0b7a45",
                      fontWeight: 800,
                    }}
                  />
                </Box>
              </Box>
            </Paper>

            <Paper elevation={0} sx={{ borderRadius: 4, border: "1px solid #e6ebf2", background: "#fff", p: 2 }}>
              <Typography variant="subtitle2" sx={{ color: "#10233f", fontWeight: 900, mb: 1.2 }}>
                {t("course.booking.additionalInfo")}
              </Typography>
              <Box
                sx={{
                  border: "1px solid #e6ebf2",
                  borderRadius: 3,
                  background: "#fff",
                  px: { xs: 1.5, sm: 2 },
                  py: 1.5,
                }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                    gap: 2,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 800, display: "block", mb: 0.8 }}>
                      {t("course.booking.serviceType")}
                    </Typography>
                    <Chip
                      label={booking.rideType || t("course.normalize.carpooling")}
                      size="small"
                      sx={{
                        borderRadius: "999px",
                        background: "#edf8ec",
                        color: "#0b7a45",
                        fontWeight: 900,
                        "& .MuiChip-label": { px: 1.4 },
                      }}
                    />
                  </Box>

                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 800, display: "block", mb: 0.8 }}>
                      {t("course.booking.comfortClass")}
                    </Typography>
                    <Chip
                      label={booking.comfortClass || "standard"}
                      size="small"
                      sx={{
                        borderRadius: "999px",
                        background: "#eef2f7",
                        color: "#334155",
                        fontWeight: 900,
                        "& .MuiChip-label": { px: 1.4 },
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </Paper>

            <Paper elevation={0} sx={{ borderRadius: 4, border: "1px solid #e6ebf2", background: "#fff", p: 2 }}>
              <Typography variant="subtitle2" sx={{ color: "#10233f", fontWeight: 900, mb: 1.2 }}>
                {t("course.booking.actions")}
              </Typography>
              <Alert
                icon={<InfoOutlined fontSize="small" />}
                severity="success"
                sx={{
                  mb: 2,
                  alignItems: "flex-start",
                  borderRadius: 3,
                  border: "1px solid #c7e7d2",
                  background: "#eef8f1",
                  color: "#175d38",
                  "& .MuiAlert-icon": {
                    color: "#0b7a45",
                    opacity: 1,
                    mt: 0.1,
                  },
                  "& .MuiAlert-message": {
                    fontWeight: 700,
                    color: "#175d38",
                  },
                }}
              >
                {t("course.booking.acceptHint")}
                <br />
                {t("course.booking.rejectHint")}
              </Alert>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <Button
                  variant="outlined"
                  onClick={onReject}
                  disabled={rejectLoading}
                  sx={{
                    flex: 1,
                    minHeight: 48,
                    borderRadius: 2.5,
                    textTransform: "none",
                    backgroundColor: "#fff",
                    fontWeight: 900,
                    borderColor: "#ff8b7f",
                    color: "#ef4444",
                    boxShadow: "none",
                    opacity: rejectLoading ? 0.75 : 1,
                    "&:hover": {
                      backgroundColor: "#fff7f6",
                      borderColor: "#ff6c5b",
                      boxShadow: "none",
                    },
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                    <CloseRounded fontSize="small" />
                    <span>{rejectLoading ? t("course.ride.waiting") : t("course.booking.reject")}</span>
                  </Stack>
                </Button>
                <Button
                  variant="contained"
                  onClick={onConfirm}
                  disabled={confirmLoading}
                  sx={{
                    flex: 1,
                    minHeight: 48,
                    borderRadius: 2.5,
                    textTransform: "none",
                    backgroundColor: "white",
                    fontWeight: 900,
                    background: "linear-gradient(135deg, #158b4a 0%, #0b7a45 100%)",
                    boxShadow: "none",
                    opacity: confirmLoading ? 0.75 : 1,
                    "&:hover": {
                      background: "linear-gradient(135deg, #127d43 0%, #09663a 100%)",
                      boxShadow: "none",
                    },
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                    <CheckRounded fontSize="small" />
                    <span>{confirmLoading ? t("course.ride.waiting") : t("course.booking.confirm")}</span>
                  </Stack>
                </Button>
              </Stack>
            </Paper>
          </Box>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default function TrajetClient() {
  const dispatch = useDispatch();
  const location = useLocation();
  const { formatMessage } = useIntl();
  const t = (id, values) => formatMessage({ id }, values);
  const routeState = location?.state || {};
  const timezone = useSelector((state) => state?.timezone?.timezone);
  const [activePanel, setActivePanel] = useState(null);
  const [origin, setOrigin] = useState("");
  const [destination, setDestination] = useState("");
  const [originQuery, setOriginQuery] = useState("");
  const [destinationQuery, setDestinationQuery] = useState("");
  const [originSuggestions, setOriginSuggestions] = useState([]);
  const [destinationSuggestions, setDestinationSuggestions] = useState([]);
  const [originLoading, setOriginLoading] = useState(false);
  const [destinationLoading, setDestinationLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [calendarMonth, setCalendarMonth] = useState(() => new Date());
  const passengerCategoryOptions = getPassengerCategoryOptions(t);
  const [passengers, setPassengers] = useState([
    { id: 1, category: passengerCategoryOptions[1], age: "10", expanded: false },
  ]);
  const [editingPassengerId, setEditingPassengerId] = useState(null);
  const [categoryMenuOpen, setCategoryMenuOpen] = useState(false);
  const [temporaryCategory, setTemporaryCategory] = useState(passengerCategoryOptions[0]);
  const [temporaryAge, setTemporaryAge] = useState("10");
  const [appliedSearch, setAppliedSearch] = useState({
    origin: "",
    destination: "",
    travelDate: "",
    passengers: 1,
  });
  const [originPlaceId, setOriginPlaceId] = useState("");
  const [destinationPlaceId, setDestinationPlaceId] = useState("");
  const [upcomingTrips, setUpcomingTrips] = useState([]);
  const [pastTrips, setPastTrips] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const isAuthenticated = useSelector((state) => state?.AuthReducer?.isAuthenticated || false);
  const authUser = useSelector((state) => state?.AuthReducer?.user || {});
  const driverId =
    routeState?.driver_id ||
    authUser?.driver_ride_details?._id ||
    authUser?.driver_ride_details?.driver_id ||
    authUser?.driver_ride_details?.id ||
    authUser?.driverRideDetails?._id ||
    authUser?.driverRideDetails?.driver_id ||
    authUser?.driverRideDetails?.id ||
    authUser?.driver_id ||
    authUser?._id ||
    authUser?.id ||
    localStorage.getItem("driver_id") ||
    localStorage.getItem("driverId") ||
    localStorage.getItem("userid") ||
    "";
  const [searchMessage, setSearchMessage] = useState("");
  const [rideDetailsOpen, setRideDetailsOpen] = useState(false);
  const [rideDetailsLoading, setRideDetailsLoading] = useState(false);
  const [rideDetailsError, setRideDetailsError] = useState("");
  const [selectedRideDetails, setSelectedRideDetails] = useState(null);
  const [bookingDetailsOpen, setBookingDetailsOpen] = useState(false);
  const [bookingDetailsLoading, setBookingDetailsLoading] = useState(false);
  const [bookingDetailsError, setBookingDetailsError] = useState("");
  const [selectedBookingDetails, setSelectedBookingDetails] = useState(null);
  const [confirmBookingLoading, setConfirmBookingLoading] = useState(false);
  const [cancelBookingLoading, setCancelBookingLoading] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmDialogMessage, setConfirmDialogMessage] = useState("");
  const [bookingSeats, setBookingSeats] = useState(1);
  const [reserveLoading, setReserveLoading] = useState(false);
  const [reserveModalOpen, setReserveModalOpen] = useState(false);
  const [authForm, setAuthForm] = useState({ email: "", password: "" });
  const [authError, setAuthError] = useState("");
  const [loadedAt, setLoadedAt] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const searchRef = useRef(null);
  const originInputRef = useRef(null);
  const destinationInputRef = useRef(null);
  const passengerEditorRef = useRef(null);

  const loadPassengerRides = useCallback(async () => {
    setIsLoading(true);
    setLoadError("");
    dispatch(showLoader(t("course.loader.loadingRides")));

    try {
      const queryParams = {
        id: routeState?.id || null,
        driver_id: routeState?.driver_id || driverId || "",
        page: 1,
        limit: "unlimited",
      };

      console.log("CourseDriver: appel /api/v1/rides/bookings", {
        driverId,
        params: queryParams,
      });
      const res = await RideApi.getRideBookingsList(queryParams);
      const payload = res?.data;
      console.log("CourseDriver: reponse /api/v1/rides/bookings", {
        status: res?.status,
        keys: payload && typeof payload === "object" ? Object.keys(payload) : [],
        payload,
      });
      console.log(res?.data)
      const rawRows = extractRideBookingRows(payload);
      console.log("CourseDriver: lignes extraites", {
        count: rawRows.length,
        sample: rawRows[0],
      });
      console.log(rawRows)
      setSearchResults(rawRows);
      setUpcomingTrips(rawRows);
      setPastTrips([]);
      setLoadedAt(new Date());
    } catch (error) {
      setSearchResults([]);
      setUpcomingTrips([]);
      setPastTrips([]);
      const status = error?.response?.status;
      const message =
        error?.response?.data?.message ||
        (status === 401 || status === 403
          ? t("course.error.sessionExpired")
          : t("course.error.loadBookings"));
      setLoadError(message);
      dispatch(
        showSnackbar({
          message,
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "error",
        })
      );
    } finally {
      setIsLoading(false);
      dispatch(hideLoader());
    }
  }, [authUser, driverId, dispatch, routeState?.driver_id, routeState?.id]);

  const loadRides = useCallback(async ({ origin_place_id, destination_place_id, departure_at }) => {
    if (!origin_place_id || !destination_place_id || !departure_at) {
      setSearchResults([]);
      setSearchMessage(t("course.error.searchMissingFields"));
      setSearchLoading(false);
      return;
    }
    setSearchMessage("");
    setSearchResults([]);
    setSearchLoading(true);
    dispatch(showLoader(t("course.loader.searching")));

    try {
      const res = await RideApi.getRideList({
        origin_place_id,
        destination_place_id,
        departure_at,
        page: 1,
        limit: "unlimited",
      });

      const payload = res?.data;
      const rawRows = Array.isArray(payload?.rides)
        ? payload.rides
        : Array.isArray(payload?.data?.data)
          ? payload.data.data
          : [];

      setSearchResults(rawRows.map(normalizeTripDetail));
      setSearchMessage(rawRows.length ? "" : t("course.error.noSearchResults"));
    } catch (error) {
      const message = error?.response?.data?.message || t("course.error.searchFailed");
      setSearchMessage(message);
      dispatch(
        showSnackbar({
          message,
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "error",
        })
      );
    } finally {
      setSearchLoading(false);
      dispatch(hideLoader());
    }
  }, [dispatch]);

  const openRideDetails = useCallback(async (trip) => {
    const rideId = trip?.id || trip?.rideId || trip?.raw?.id || trip?.raw?.ride_id;
    if (!rideId) {
      dispatch(
        showSnackbar({
          message: t("course.error.openRideDetails"),
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "error",
        })
      );
      return;
    }

    setRideDetailsOpen(true);
    setRideDetailsLoading(true);
    setRideDetailsError("");
    setSelectedRideDetails(normalizeTripDetail(trip, timezone, t));

    try {
      const response = await RideApi.getRideById(rideId);
      const payload = response?.data?.data || response?.data || {};
      setSelectedRideDetails(normalizeTripDetail(payload, timezone, t));
    } catch (error) {
      const message = error?.response?.data?.message || t("course.error.loadRideDetails");
      setRideDetailsError(message);
      dispatch(
        showSnackbar({
          message,
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "error",
        })
      );
    } finally {
      setRideDetailsLoading(false);
    }
  }, [dispatch, timezone]);

  const closeRideDetails = useCallback(() => {
    setRideDetailsOpen(false);
    setRideDetailsLoading(false);
    setRideDetailsError("");
    setSelectedRideDetails(null);
    setBookingSeats(1);
  }, []);

  const closeBookingDetails = useCallback(() => {
    setBookingDetailsOpen(false);
    setBookingDetailsLoading(false);
    setBookingDetailsError("");
    setSelectedBookingDetails(null);
  }, []);

  const closeConfirmDialog = useCallback(() => {
    setConfirmDialogOpen(false);
    setConfirmDialogMessage("");
  }, []);

  const openBookingDetails = useCallback(
    async (trip) => {
      const bookingId = trip?.bookingId || trip?.raw?.booking_id || trip?.raw?.id || trip?.id;

      if (!bookingId || bookingId === "-") {
        dispatch(
          showSnackbar({
            message: t("course.error.loadBookingDetails"),
            autoHideDuration: 3000,
            anchorOrigin: { vertical: "top", horizontal: "right" },
            variant: "error",
          })
        );
        return;
      }

      setBookingDetailsOpen(true);
      setBookingDetailsLoading(true);
      setBookingDetailsError("");
      setSelectedBookingDetails(normalizeBookingDetail(trip, timezone, t));

      try {
        const response = await BookingApi.getBookingById(bookingId);
        const payload = response?.data?.data ?? response?.data ?? {};
        setSelectedBookingDetails(normalizeBookingDetail(payload, timezone, t));
      } catch (error) {
        const message = error?.response?.data?.message || t("course.error.loadBookingDetails2");
        setBookingDetailsError(message);
        dispatch(
          showSnackbar({
            message,
            autoHideDuration: 3000,
            anchorOrigin: { vertical: "top", horizontal: "right" },
            variant: "error",
          }) 
        );
      } finally {
        setBookingDetailsLoading(false);
      }
    },
    [dispatch, timezone]
  );

  const confirmBooking = useCallback(async () => {
    const bookingId = selectedBookingDetails?.bookingId || selectedBookingDetails?.id;

    if (!bookingId || bookingId === "-") {
      dispatch(
        showSnackbar({
          message: t("course.error.confirmBooking"),
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "error",
        })
      );
      return;
    }

    setConfirmBookingLoading(true);
    try {
      const response = await BookingApi.confirmBooking(bookingId);
      const message = response?.data?.message || t("course.success.bookingConfirmed");
      setConfirmDialogMessage(message);
      setBookingDetailsOpen(false);
      setBookingDetailsError("");
      setSelectedBookingDetails(null);
      setConfirmDialogOpen(true);
      dispatch(
        showSnackbar({
          message,
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "success",
        })
      );
      await loadPassengerRides();
    } catch (error) {
      const message = error?.response?.data?.message || t("course.error.confirmBookingFailed");
      dispatch(
        showSnackbar({
          message,
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "error",
        })
      );
    } finally {
      setConfirmBookingLoading(false);
    }
  }, [dispatch, loadPassengerRides, selectedBookingDetails?.bookingId, selectedBookingDetails?.id]);

  const cancelBooking = useCallback(async () => {
    const bookingId = selectedBookingDetails?.bookingId || selectedBookingDetails?.id;

    if (!bookingId || bookingId === "-") {
      dispatch(
        showSnackbar({
          message: t("course.error.rejectBooking"),
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "error",
        })
      );
      return;
    }

    setCancelBookingLoading(true);
    try {
      const response = await BookingApi.cancelBooking(bookingId);
      const message = response?.data?.message || t("course.success.bookingRejected");
      setConfirmDialogMessage(message);
      setBookingDetailsOpen(false);
      setBookingDetailsError("");
      setSelectedBookingDetails(null);
      setConfirmDialogOpen(true);
      dispatch(
        showSnackbar({
          message,
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "success",
        })
      );
      await loadPassengerRides();
    } catch (error) {
      const message = error?.response?.data?.message || t("course.error.rejectBookingFailed");
      dispatch(
        showSnackbar({
          message,
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "error",
        })
      );
    } finally {
      setCancelBookingLoading(false);
    }
  }, [dispatch, loadPassengerRides, selectedBookingDetails?.bookingId, selectedBookingDetails?.id]);

  useEffect(() => {
    const availableSeats = Number(selectedRideDetails?.availableSeats ?? 0);
    setBookingSeats(availableSeats > 0 ? availableSeats : 1);
  }, [selectedRideDetails?.id, selectedRideDetails?.availableSeats]);

  const buildBookingPayload = useCallback(() => {
    const ride = selectedRideDetails?.raw || selectedRideDetails || {};
    const passengerCount = Math.max(1, Number(bookingSeats || 1));
    const seatPrice = Number(ride?.price_per_seat ?? selectedRideDetails?.pricePerSeat ?? 0);
    const serviceFee = Number(ride?.service_fee ?? selectedRideDetails?.serviceFee ?? 0);

    return {
      ride_id: ride?.ride_id || ride?.id || selectedRideDetails?.id || "",
      pickup_waypoint_id: ride?.pickup_waypoint_id || "",
      dropoff_waypoint_id: ride?.dropoff_waypoint_id || "",
      seats_booked: passengerCount,
      total_amount: seatPrice * passengerCount,
      service_fee: serviceFee,
      currency: ride?.currency || selectedRideDetails?.currency || "XAF",
    };
  }, [bookingSeats, selectedRideDetails]);

  const reserveRide = useCallback(async () => {
    const payload = buildBookingPayload();
    if (!payload.ride_id) {
      dispatch(
        showSnackbar({
          message: t("course.error.reserveRide"),
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "error",
        })
      );
      return;
    }

    dispatch(showLoader("Loading please wait..."));
    setReserveLoading(true);
    try {
      const response = await BookingApi.createBooking(payload);
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: response?.data?.message || t("course.success.reservationDone"),
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "success",
        })
      );
      setReserveModalOpen(false);
      const bookingData = response?.data?.data || response?.data || null;
      const confirmationPayload = {
        booking: bookingData,
        ride: selectedRideDetails?.raw || selectedRideDetails || null,
        passengerCount: Math.max(1, Number(bookingSeats || 1)),
        passengerEmail:
          bookingData?.user?.email ||
          bookingData?.email ||
          authUser?.email ||
          localStorage.getItem("email") ||
          "",
      };
      sessionStorage.setItem("bookingConfirmationPayload", JSON.stringify(confirmationPayload));
      window.location.assign("/booking-confirmation");
    } catch (error) {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: error?.response?.data?.message || t("course.error.reservationFailed"),
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "error",
        })
      );
    } finally {
      setReserveLoading(false);
    }
  }, [buildBookingPayload, dispatch, bookingSeats, selectedRideDetails, authUser?.email]);

  const handleReserveNow = useCallback(() => {
    if (!isAuthenticated) {
      setAuthError("");
      setReserveModalOpen(true);
      return;
    }
    void reserveRide();
  }, [isAuthenticated, reserveRide]);

  const handleAuthSubmit = useCallback(async (event) => {
    event.preventDefault();
    setAuthError("");
    setReserveLoading(true);

    try {
      const res = await loginApi.login({ email: authForm.email, password: authForm.password });
      const authData = getAuthPayload(res?.data);
      const token = persistAuthSession(authData);
      if (!token) {
        throw new Error(t("course.auth.loginImpossible"));
      }

      dispatch(setAuthentication(authData));
      setReserveModalOpen(false);
      await reserveRide();
    } catch (error) {
      const message = error?.response?.data?.message || error?.message || t("course.auth.invalidCredentials");
      setAuthError(message);
      dispatch(
        showSnackbar({
          message,
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "error",
        })
      );
    } finally {
      setReserveLoading(false);
    }
  }, [authForm.email, authForm.password, dispatch, reserveRide]);

  useEffect(() => {
    loadPassengerRides();
  }, [loadPassengerRides]);

  useEffect(() => {
    if (!activePanel && !editingPassengerId) return undefined;

    const handleOutsideClick = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setActivePanel(null);
      }

      if (editingPassengerId && !passengerEditorRef.current?.contains(event.target)) {
        setEditingPassengerId(null);
        setCategoryMenuOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setActivePanel(null);
        setEditingPassengerId(null);
        setCategoryMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [activePanel, editingPassengerId]);

  useEffect(() => {
    const currentQuery = activePanel === "origin"
      ? originQuery
      : activePanel === "destination"
        ? destinationQuery
        : "";

    if (!activePanel || currentQuery.trim().length < 2) {
      if (activePanel === "origin") setOriginSuggestions([]);
      if (activePanel === "origin") setOriginLoading(false);
      if (activePanel === "destination") setDestinationSuggestions([]);
      if (activePanel === "destination") setDestinationLoading(false);
      return undefined;
    }

    const panel = activePanel;
    const query = currentQuery.trim();
    let isActive = true;

    const timer = window.setTimeout(() => {
      const loadPlaces = async () => {
        try {
          if (!isActive) return;
          if (panel === "origin") setOriginLoading(true);
          if (panel === "destination") setDestinationLoading(true);

          const response = await PlaceService.getPlaceList({ search: query });
          const results = response?.data?.data?.data || [];
          const normalized = results.map(formatPlaceResult);

          if (!isActive) return;
          if (panel === "origin") setOriginSuggestions(normalized);
          if (panel === "destination") setDestinationSuggestions(normalized);
        } catch (error) {
          if (!isActive) return;
          if (panel === "origin") setOriginSuggestions([]);
          if (panel === "destination") setDestinationSuggestions([]);
        } finally {
          if (!isActive) return;
          if (panel === "origin") setOriginLoading(false);
          if (panel === "destination") setDestinationLoading(false);
        }
      };

      loadPlaces();
    }, 300);

    return () => {
      isActive = false;
      window.clearTimeout(timer);
    };
  }, [activePanel, originQuery, destinationQuery]);

  const today = new Date();
  const isToday = selectedDate.toDateString() === today.toDateString();
  const dateLabel = isToday
    ? `Aujourd'hui, ${formatFrenchDate(selectedDate, "short")}`
    : formatFrenchDate(selectedDate, "short");
  const monthCells = buildMonthGrid(calendarMonth);

  const openPanel = (panel) => {
    setActivePanel((current) => {
      const nextPanel = current === panel ? null : panel;

      if (nextPanel === "origin") {
        setOriginQuery((currentQuery) => currentQuery || origin);
        window.setTimeout(() => originInputRef.current?.focus(), 0);
      }

      if (nextPanel === "destination") {
        setDestinationQuery((currentQuery) => currentQuery || destination);
        window.setTimeout(() => destinationInputRef.current?.focus(), 0);
      }

      return nextPanel;
    });
  };

  const goToPreviousMonth = () => {
    setCalendarMonth((current) => new Date(current.getFullYear(), current.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCalendarMonth((current) => new Date(current.getFullYear(), current.getMonth() + 1, 1));
  };

  const updatePassenger = (id, patch) => {
    setPassengers((current) => current.map((passenger) => (
      passenger.id === id ? { ...passenger, ...patch } : passenger
    )));
  };

  const savePassengerEditor = () => {
    if (!editingPassengerId) return;
    updatePassenger(editingPassengerId, { category: temporaryCategory, age: temporaryAge });
    setEditingPassengerId(null);
    setCategoryMenuOpen(false);
  };

  const deletePassenger = () => {
    if (!editingPassengerId) return;
    setPassengers((current) => current.filter((passenger) => passenger.id !== editingPassengerId));
    setEditingPassengerId(null);
    setCategoryMenuOpen(false);
  };

  const selectPlace = (panel, place) => {
    if (panel === "origin") {
      setOrigin(place.title);
      setOriginQuery(place.title);
      setOriginPlaceId(place.id);
    }
    if (panel === "destination") {
      setDestination(place.title);
      setDestinationQuery(place.title);
      setDestinationPlaceId(place.id);
    }
    setActivePanel(null);
  };

  const appliedDate = formatDateForApi(selectedDate);
  const filteredTrips = useMemo(() => {
    const normalizeSearch = (value) => String(value || "").trim().toLowerCase();
    const originQueryValue = normalizeSearch(appliedSearch.origin);
    const destinationQueryValue = normalizeSearch(appliedSearch.destination);

    const matchesTrip = (trip) => {
      const originValue = normalizeSearch(
        trip?.originLabel ||
          trip?.origin_place?.name ||
          trip?.origin_place_name ||
          ""
      );
      const destinationValue = normalizeSearch(
        trip?.destinationLabel ||
          trip?.destination_place?.name ||
          trip?.destination_place_name ||
          ""
      );
      const departureValue = trip?.departureAt
        ? moment(trip.departureAt).tz(timezone || "Africa/Douala").format("YYYY-MM-DD")
        : "";

      const originOk = !originQueryValue || originValue.includes(originQueryValue);
      const destinationOk = !destinationQueryValue || destinationValue.includes(destinationQueryValue);
      const dateOk = !appliedSearch.travelDate || departureValue === appliedSearch.travelDate;
      return originOk && destinationOk && dateOk;
    };

    return {
      upcoming: upcomingTrips.filter(matchesTrip),
      past: pastTrips.filter(matchesTrip),
    };
  }, [
    appliedSearch.destination,
    appliedSearch.origin,
    appliedSearch.travelDate,
    pastTrips,
    timezone,
    upcomingTrips,
  ]);

  const recentSearches = useMemo(() => {
    const toLabel = (trip) => ({
      from:
        trip?.originLabel ||
        trip?.origin_place?.name ||
        trip?.origin_place_name ||
        "D?part",
      to:
        trip?.destinationLabel ||
        trip?.destination_place?.name ||
        trip?.destination_place_name ||
        "Arriv?e",
      date: trip?.departureAt
        ? moment(trip.departureAt).tz(timezone || "Africa/Douala").format("D MMM")
        : "Aujourd'hui",
    });

    const combined = [...filteredTrips.upcoming, ...filteredTrips.past].slice(0, 3).map(toLabel);
    return combined.length > 0 ? combined : recentFallback;
  }, [filteredTrips.past, filteredTrips.upcoming, timezone]);

  const handleSearch = (event) => {
    event?.preventDefault?.();
    setAppliedSearch({
      origin: (originQuery || origin).trim(),
      destination: (destinationQuery || destination).trim(),
      travelDate: appliedDate,
      passengers: passengers.length,
    });
    void loadRides({
      origin_place_id: originPlaceId || originQuery || origin,
      destination_place_id: destinationPlaceId || destinationQuery || destination,
      departure_at: appliedDate,
    });
  };

  const editingPassenger = passengers.find((passenger) => passenger.id === editingPassengerId) || null;

  const lastUpdatedLabel = loadedAt
    ? moment(loadedAt).tz(timezone || "Africa/Douala").format("DD/MM/YYYY HH:mm")
    : "--";

  return (
    <>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 4,
                  p: { xs: 2.25, md: 3 },
                  border: "1px solid rgba(15, 39, 72, 0.08)",
                  background: "linear-gradient(135deg, rgb(19 145 83 / 97%) 0%, rgb(25 103 16 / 98%) 100%)",
                  color: "#fff",
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    inset: "auto -20px -30px auto",
                    width: 220,
                    height: 200,
                    borderRadius: "50%",
                    background: "rgba(255,255,255,0.08)",
                  }}
                />
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={2}
                  alignItems={{ xs: "flex-start", md: "center" }}
                  justifyContent="space-between"
                  sx={{ position: "relative", zIndex: 1 }}
                >
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 900, lineHeight: 1.05, mt: 0.5 }}>
                      {t("course.page.title")}
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 1, maxWidth: 760, color: "rgba(255,255,255,0.88)" }}>
                      {t("course.page.subtitle")}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
      <Box
        sx={{
          display: "grid",
          gap: 3,
          pb: 3,
          pt: { xs: 1, md: 0.5 },
        }}
      >
        

       

        {editingPassenger ? (
          <div className="bbc-passenger-modal-backdrop">
            <div className="bbc-passenger-modal" ref={passengerEditorRef} role="dialog" aria-modal="true" aria-label={t("course.passengerModal.title")}>
              <button
                type="button"
                className="bbc-passenger-modal-close"
                aria-label={t("course.passengerModal.close")}
                onClick={() => {
                  setEditingPassengerId(null);
                  setCategoryMenuOpen(false);
                }}
              >
                <span />
                <span />
              </button>

              <h3>{t("course.passengerModal.title")}</h3>

              <div className="bbc-passenger-modal-field">
                <label>{t("course.passengerModal.category")}</label>
                <button
                  type="button"
                  className="bbc-passenger-modal-select"
                  onClick={() => setCategoryMenuOpen((current) => !current)}
                >
                  <span>{temporaryCategory}</span>
                  <ChevronDownIcon />
                </button>
                {categoryMenuOpen ? (
                  <div className="bbc-passenger-modal-menu" role="listbox" aria-label={t("course.passengerModal.category")}>
                    {passengerCategoryOptions.map((option) => (
                      <button
                        key={option}
                        type="button"
                        className={`bbc-passenger-modal-option ${temporaryCategory === option ? "is-selected" : ""}`}
                        onClick={() => {
                          setTemporaryCategory(option);
                          updatePassenger(editingPassenger.id, { category: option });
                          setCategoryMenuOpen(false);
                        }}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>

              <div className="bbc-passenger-modal-field">
                <label>{t("course.passengerModal.age")}</label>
                <Select
                  fullWidth
                  value={temporaryAge}
                  onChange={(event) => {
                    const nextAge = String(event.target.value);
                    setTemporaryAge(nextAge);
                    updatePassenger(editingPassenger.id, { age: nextAge });
                  }}
                  displayEmpty
                  variant="outlined"
                  IconComponent={ChevronDownIcon}
                  sx={{
                    mt: 1,
                    borderRadius: 3,
                    background: "#f8fbff",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#b8c9e6",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#7ea0d6",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#4f7fd1",
                    },
                    "& .MuiSelect-select": {
                      py: 1.4,
                      px: 1.8,
                      fontWeight: 700,
                      color: "#10233f",
                    },
                    "& .MuiSvgIcon-root": {
                      color: "#52627d",
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 280,
                        borderRadius: 3,
                        mt: 1,
                      },
                    },
                  }}
                >
                  {passengerAgeOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </div>

              <div className="bbc-passenger-modal-actions">
                <button type="button" className="bbc-passenger-modal-delete" onClick={deletePassenger}>
                  {t("course.passengerModal.delete")}
                </button>
                <button type="button" className="bbc-passenger-modal-save" onClick={savePassengerEditor}>
                  {t("course.passengerModal.close")}
                </button>
              </div>
            </div>
          </div>
        ) : null}
        <Box sx={{ display: "grid", gap: 1.5 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Typography variant="h6" sx={{ color: "#10233f", fontWeight: 900 }}>
              {t("course.results.found")}
            </Typography>
            <Typography variant="body2" sx={{ color: "#6a778a", fontWeight: 700 }}>
              {t("course.results.count", { count: searchResults.length })}
            </Typography>
          </Stack>

          {searchLoading ? (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 4,
                p: 3,
                border: "1px solid rgba(15, 39, 72, 0.08)",
                display: "grid",
                placeItems: "center",
                gap: 2,
                background: "#fff",
              }}
            >
              <CircularProgress size={28} />
              <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 700 }}>
                {t("course.loader.searchInProgress")}
              </Typography>
            </Paper>
          ) : searchResults.length ? (
            <CourseResultsTable
              rows={searchResults}
              timezone={timezone}
              onOpenDetails={openRideDetails}
              onConfirmCourse={openBookingDetails}
              loading={searchLoading}
            />
          ) : (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 4,
                p: 3,
                border: "1px solid rgba(15, 39, 72, 0.08)",
                background: "#fff",
              }}
            >
              <Typography variant="body1" sx={{ color: "#10233f", fontWeight: 900 }}>
                {t("course.results.empty")}
              </Typography>
              <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 600, mt: 0.75 }}>
                {searchMessage || t("course.results.emptyDesc")}
              </Typography>
            </Paper>
          )}
        </Box>

        
        {loadError ? <Alert severity="error">{loadError}</Alert> : null}

        {isLoading ? (
          <Paper
            elevation={0}
            sx={{
              borderRadius: 4,
              p: 4,
              border: "1px solid rgba(15, 39, 72, 0.08)",
              display: "grid",
              placeItems: "center",
              gap: 2,
              background: "#fff",
            }}
          >
            <CircularProgress />
            <Typography variant="body1" sx={{ color: "#64748b", fontWeight: 700 }}>
              {t("course.loader.loadingCourses")}
            </Typography>
          </Paper>
        ) : null}

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "#6a778a", fontSize: 12, fontWeight: 700 }}>
          <RefreshRounded fontSize="small" />
          <span>{t("course.lastUpdate", { date: lastUpdatedLabel })}</span>
        </Box>

        <RideDetailsModal
          open={rideDetailsOpen}
          ride={selectedRideDetails}
          loading={rideDetailsLoading}
          error={rideDetailsError}
          onClose={closeRideDetails}
          passengerCount={appliedSearch.passengers}
          bookingSeats={bookingSeats}
          onBookingSeatsChange={(value) => {
            const maxSeats = Number(selectedRideDetails?.availableSeats ?? 1);
            const normalized = Math.max(1, Math.min(Number(value) || 1, maxSeats || 1));
            setBookingSeats(normalized);
          }}
          onReserveNow={handleReserveNow}
          reserveLoading={reserveLoading}
        />

        <BookingDetailsModal
          open={bookingDetailsOpen}
          booking={selectedBookingDetails}
          loading={bookingDetailsLoading}
          error={bookingDetailsError}
          onClose={closeBookingDetails}
          onConfirm={confirmBooking}
          onReject={cancelBooking}
          confirmLoading={confirmBookingLoading}
          rejectLoading={cancelBookingLoading}
        />

        <Dialog
          open={confirmDialogOpen}
          onClose={closeConfirmDialog}
          fullWidth
          maxWidth="xs"
          PaperProps={{ sx: { borderRadius: 4 } }}
        >
          <DialogTitle sx={{ fontWeight: 900, color: "#10233f" }}>{t("course.success.title")}</DialogTitle>
          <DialogContent>
            <Alert severity="success" sx={{ mt: 1 }}>
              {confirmDialogMessage || t("course.success.bookingConfirmed")}
            </Alert>
          </DialogContent>
          <Box sx={{ px: 2.5, pb: 2.5, display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              onClick={closeConfirmDialog}
              sx={{
                borderRadius: 3,
                textTransform: "none",
                fontWeight: 900,
                background: "linear-gradient(135deg, #0f8a48 0%, #0b6e39 100%)",
                "&:hover": {
                  background: "linear-gradient(135deg, #0d7a40 0%, #0a6233 100%)",
                },
              }}
            >
              {t("course.auth.close")}
            </Button>
          </Box>
        </Dialog>

        <ReservationLoginModal
          open={reserveModalOpen}
          onClose={() => setReserveModalOpen(false)}
          form={authForm}
          setForm={setAuthForm}
          onSubmit={handleAuthSubmit}
          loading={reserveLoading}
          error={authError}
        />

      </Box>
    </>
  );
}

function formatDateForApi(date) {
  if (!date) return "";
  if (typeof date === "string") return date.slice(0, 10);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}
 
